import React, { useEffect } from 'react';

import { DialogProps } from '@ast/magma/components/dialog';
import { DialogButtonType, OnCloseHandler } from '@ast/magma/components/dialog/dialog';

import { GenericErrorBoundary } from '@app/common/errors/GenericErrorBoundary/GenericErrorBoundary';
import { checkIfMaintenanceModeError } from '@app/common/errors/genericErrorHandler';

import { t } from '@app/core/i18n';

import { ErrorDialog } from '../ErrorDialog';

import { CommonErrorDialogFooter } from '../ErrorDialog/CommonFooter/CommonFooter';

/**
 * Dialog wrapper properties.
 */
export interface DialogWrapperProps {
  visible: boolean;
  /**
   * Callback when error occurs while showing dialog.
   * Usially to be used to hide dialog (turn off visible switch).
   */
  onRecover?:OnCloseHandler;
  children: React.ReactElement<DialogProps>;
}

export const Redirect = () => {
  useEffect(() => {
    window.location.assign('/');
  }, []);

  return null;
};

/**
 * Dialog wrapper allow to control dialog visibility and error handling.
 * If error occurre in initialing the dialog then error will be shown.
 * @param props dialog wrapper props.
 */
export const DialogWrapper: React.FC<DialogWrapperProps> = (props) => {
  const {
    visible,
    children,
    onRecover,
  } = props;

  /**
   * We must ensure that dialog onClose handler will be called
   * regardless an error occured on not.
   */
  const onErrorDialogClose = onRecover || children.props.onClose;
  if (!onErrorDialogClose) {
    // eslint-disable-next-line i18next/no-literal-string
    console.error('Dialog onClose or onRecover must be specified');
  }

  return visible ? (
    <GenericErrorBoundary
      fallback={(error) => {
        const isMaintenanceMode = checkIfMaintenanceModeError(error);

        if (isMaintenanceMode) {
          return (<Redirect />);
        }

        return (
          <ErrorDialog
            error={error}
            onClose={onErrorDialogClose}
            buttons={[{
              type: DialogButtonType.Close,
              label: t('error-dialog.close-button|Error dialog close button', 'Close'),
            }]}
            defaultButton={DialogButtonType.Close}
            footer={<CommonErrorDialogFooter isInternalError={false} />}
          />
        );
      }}
    >
      {children}
    </GenericErrorBoundary>
  ) : null;
};

DialogWrapper.defaultProps = {
  visible: false,
};
